import { useState } from 'react';
import { ApiError, projects, type ProjectFull } from '../api.js';
import { useToast } from './ui/toast.js';
import { Button } from './ui/button.js';

const DEFAULT_LUFS = -16;
const DEFAULT_XFADE = 50;

interface Props {
  project: ProjectFull;
  onUpdated: (next: ProjectFull) => void;
}

export function AudioSettingsPanel({ project, onUpdated }: Props) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [lufs, setLufs] = useState<number>(project.lufs_target ?? DEFAULT_LUFS);
  const [xfade, setXfade] = useState<number>(project.crossfade_ms ?? DEFAULT_XFADE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(useDefaults: boolean) {
    setSaving(true);
    setError(null);
    try {
      const r = await projects.updateAudioSettings(
        project.id,
        useDefaults ? null : lufs,
        useDefaults ? null : xfade,
      );
      onUpdated(r.project);
      if (useDefaults) {
        setLufs(DEFAULT_LUFS);
        setXfade(DEFAULT_XFADE);
      }
      toast.success(
        useDefaults ? 'Reset to defaults' : 'Audio settings saved',
        'Applies on the next render.',
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to save.';
      setError(msg);
      toast.error('Could not save settings', msg);
    } finally {
      setSaving(false);
    }
  }

  const isDefault = project.lufs_target === null && project.crossfade_ms === null;
  const summary = isDefault
    ? `${DEFAULT_LUFS} LUFS · ${DEFAULT_XFADE}ms crossfade (defaults)`
    : `${project.lufs_target ?? DEFAULT_LUFS} LUFS · ${project.crossfade_ms ?? DEFAULT_XFADE}ms crossfade`;

  return (
    <div className="border-t border-soft pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-xs text-muted hover:text-ink"
      >
        <span className="uppercase tracking-wider">Audio polish</span>
        <span className="font-mono normal-case tracking-normal">{open ? '−' : '+'}</span>
      </button>
      <p className="text-[11px] text-muted/70 mt-1">{summary}</p>
      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-[11px] text-muted mb-1">
              Loudness target (LUFS) <span className="text-muted/60">— quieter ↔ louder</span>
            </label>
            <input
              type="range"
              min={-30}
              max={-8}
              step={0.5}
              value={lufs}
              onChange={(e) => setLufs(parseFloat(e.target.value))}
              className="range-accent"
            />
            <div className="flex justify-between text-[10px] text-muted/60 font-mono">
              <span>-30</span>
              <span>{lufs.toFixed(1)}</span>
              <span>-8</span>
            </div>
            <p className="text-[10px] text-muted/60 mt-0.5">
              Podcasts/audiobooks: -16. Streaming services: -14. Quiet meditation: -23.
            </p>
          </div>
          <div>
            <label className="block text-[11px] text-muted mb-1">
              Crossfade between sections (ms)
            </label>
            <input
              type="range"
              min={0}
              max={500}
              step={10}
              value={xfade}
              onChange={(e) => setXfade(parseInt(e.target.value, 10))}
              className="range-accent"
            />
            <div className="flex justify-between text-[10px] text-muted/60 font-mono">
              <span>0</span>
              <span>{xfade}ms</span>
              <span>500</span>
            </div>
            <p className="text-[10px] text-muted/60 mt-0.5">
              0 = hard cuts. 30–80 = smooth provider seams. 200+ = blended.
            </p>
          </div>
          {error && (
            <div className="text-xs text-accent bg-accent/5 border border-accent/20 rounded px-2 py-1.5">
              {error}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="primary"
              onClick={() => save(false)}
              loading={saving}
              loadingText="Saving…"
              className="flex-1 text-xs py-1.5"
            >
              Save
            </Button>
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving}
              className="text-xs text-muted hover:text-ink underline-offset-2 hover:underline px-2 disabled:opacity-40"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
